'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCurrency, formatNumber } from '@/app/ops/helpers/formatters';
import { formatPercentual } from '@/app/relatorios/helpers/formatRelatorios';
import { cn } from '@/lib/utils';

import { IMarcaDaVisao, MARCAS_VISIVEIS } from '../helpers/visaoExecutiva';
import { carteiraDaMarcaHref } from '../helpers/visaoExecutivaLinks';
import { BarraSegmentada } from './BarraSegmentada';
import { BlocoCard } from './BlocoCard';
import { EstadoDoBloco } from './EstadoDoBloco';

const LINHA = 'flex w-full flex-col gap-1 rounded-md px-2 py-1.5';

/**
 * As duas partes da barra, na ordem em que ela as desenha. A legenda lê esta MESMA lista:
 * uma legenda que nomeia o próprio tom mente assim que a barra muda de cor.
 */
const FATIAS_DA_BARRA = [
  { chave: 'risco', classe: 'bg-danger' },
  // O MESMO verde do "tem folga" da ocupação: sair no prazo é a notícia boa do produto
  // inteiro. Era a cor da marca (`bg-brand`), que aqui lia como "roxo quer dizer o quê?".
  { chave: 'noPrazo', classe: 'bg-ocupacao-folga' },
] as const;

type TChaveDaFatia = (typeof FATIAS_DA_BARRA)[number]['chave'];

interface IConteudoDaMarcaProps {
  marca: IMarcaDaVisao;
  /** A maior carteira da lista: é ela que vale a barra inteira, para as marcas se compararem. */
  maiorFaturamento: number;
}

/** Uma marca: a carteira dela como barra, com a parte em risco por cima, e os números dela. */
function ConteudoDaMarca({ marca, maiorFaturamento }: IConteudoDaMarcaProps) {
  const { t } = useTranslation('common');

  const nome = marca.marca === '' ? t('visaoExecutiva.marcas.semMarca') : marca.marca;

  /** A carteira da marca repartida nas duas partes que a barra desenha. */
  const valorDaFatia: Readonly<Record<TChaveDaFatia, number>> = {
    risco: marca.riscoValor,
    noPrazo: Math.max(0, marca.faturamento - marca.riscoValor),
  };

  return (
    <>
      <div className="flex w-full items-baseline justify-between gap-2">
        <span className="truncate text-xs font-semibold text-foreground">{nome}</span>
        <span className="shrink-0 text-xs tabular-nums text-foreground-subtle">
          {marca.maiorAtraso > 0
            ? t('visaoExecutiva.marcas.opsComAtraso', {
                count: marca.ops,
                dias: formatNumber(marca.maiorAtraso),
              })
            : t('visaoExecutiva.marcas.ops', { count: marca.ops })}
        </span>
      </div>

      {/* A parte em risco vem PRIMEIRO, à esquerda: assim as fatias vermelhas ficam
          alinhadas entre as marcas e dá para compará-las de um olhar. */}
      <BarraSegmentada
        total={maiorFaturamento}
        segmentos={FATIAS_DA_BARRA.map((fatia) => ({ ...fatia, valor: valorDaFatia[fatia.chave] }))}
        rotulo={t('visaoExecutiva.marcas.barra', {
          marca: nome,
          risco: formatCurrency(marca.riscoValor),
          carteira: formatCurrency(marca.faturamento),
        })}
      />

      <span className="text-xs tabular-nums text-foreground-muted">
        {t('visaoExecutiva.marcas.valores', {
          risco: formatCurrency(marca.riscoValor),
          carteira: formatCurrency(marca.faturamento),
          pct: formatPercentual(marca.pctRisco),
        })}
      </span>
    </>
  );
}

interface MarcasBlocoProps {
  marcas: Array<IMarcaDaVisao>;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * A performance por MARCA, maior carteira primeiro: é por marca que a confecção é cobrada
 * e é por ela que se decide a quem ligar. A lista abre nas maiores, e "ver todas" mostra o
 * resto sem levar para outra tela.
 */
export function MarcasBloco({ marcas, loading, failed, onRetry, className }: MarcasBlocoProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [todas, setTodas] = useState(false);

  const visiveis = todas ? marcas : marcas.slice(0, MARCAS_VISIVEIS);
  const escondidas = marcas.length - visiveis.length;
  const maiorFaturamento = marcas[0]?.faturamento ?? 0;

  return (
    <BlocoCard
      pergunta={t('visaoExecutiva.marcas.pergunta')}
      fonte={t('visaoExecutiva.marcas.fonte')}
      className={className}
    >
      <EstadoDoBloco
        loading={loading}
        failed={failed}
        vazio={marcas.length === 0}
        vazioTexto={t('visaoExecutiva.marcas.vazio')}
        onRetry={onRetry}
      >
        {/* A legenda ANTES das barras: são duas cores só, e sem elas a barra de cada marca
            é um desenho sem unidade. */}
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {FATIAS_DA_BARRA.map((fatia) => (
            <li key={fatia.chave} className="flex items-center gap-1.5">
              <span className={cn('size-2.5 shrink-0 rounded-sm', fatia.classe)} aria-hidden />
              <span className="text-xs text-foreground-subtle">
                {t(`visaoExecutiva.marcas.legenda.${fatia.chave}`)}
              </span>
            </li>
          ))}
        </ul>

        <ul className="flex animate-fade-in flex-col gap-1">
          {visiveis.map((marca) => (
            <li key={marca.marca} className="flex min-w-0">
              {/* A OP sem marca preenchida não tem recorte para abrir na Carteira: ela
                  continua na conta, mas a linha dela não é um atalho. */}
              {marca.marca === '' ? (
                <div className={LINHA}>
                  <ConteudoDaMarca marca={marca} maiorFaturamento={maiorFaturamento} />
                </div>
              ) : (
                <ControlledTooltip content={t('visaoExecutiva.marcas.irParaMarca', { marca: marca.marca })}>
                  <ControlledButton
                    defaultHtmlButton
                    type="button"
                    onClick={() => router.push(carteiraDaMarcaHref(marca.marca))}
                    className={cn(LINHA, 'interactive text-left hover:bg-muted')}
                  >
                    <ConteudoDaMarca marca={marca} maiorFaturamento={maiorFaturamento} />
                  </ControlledButton>
                </ControlledTooltip>
              )}
            </li>
          ))}
        </ul>

        {escondidas > 0 && (
          <ControlledButton
            variant="text"
            tone="brand"
            size="sm"
            onClick={() => setTodas(true)}
            className="mt-auto w-fit normal-case"
          >
            {t('visaoExecutiva.marcas.verTodas', { n: escondidas })}
          </ControlledButton>
        )}
      </EstadoDoBloco>
    </BlocoCard>
  );
}
